import * as TabsPrimitive from '@radix-ui/react-tabs';
import { cva, type VariantProps } from 'class-variance-authority';
import * as React from 'react';

import { cn } from '../../styles/lib/utils';

const tabsTriggerVariants = cva(
  'inline-flex items-center whitespace-nowrap border-b-2 border-transparent pb-3 text-sm font-medium cursor-pointer',
  {
    variants: {
      color: {
        blue: [
          'text-grey-70 hover:text-grey-90',
          'data-[state=active]:border-blue-50 data-[state=active]:text-blue-50 data-[state=active]:font-semibold',
        ],
        red: [
          'text-grey-70 hover:text-grey-90',
          'data-[state=active]:border-red-50 data-[state=active]:text-red-50 data-[state=active]:font-semibold',
        ],
        green: [
          'text-grey-70 hover:text-grey-90',
          'data-[state=active]:border-green-50 data-[state=active]:text-green-50 data-[state=active]:font-semibold',
        ],
        orange: [
          'text-grey-70 hover:text-grey-90',
          'data-[state=active]:border-orange-50 data-[state=active]:text-orange-50 data-[state=active]:font-semibold',
        ],
        yellow: [
          'text-grey-70 hover:text-grey-90',
          'data-[state=active]:border-yellow-60 data-[state=active]:text-yellow-60 data-[state=active]:font-semibold',
        ],
        grey: [
          'text-grey-70 hover:text-grey-90',
          'data-[state=active]:border-grey-90 data-[state=active]:text-grey-90 data-[state=active]:font-semibold',
        ],
      },
    },
    defaultVariants: {
      color: 'blue',
    },
  },
);

type TabsColor = VariantProps<typeof tabsTriggerVariants>['color'];

const TabsColorContext = React.createContext<TabsColor>('blue');

function Tabs({ ...props }: React.ComponentProps<typeof TabsPrimitive.Root>) {
  return <TabsPrimitive.Root data-slot="tabs" {...props} />;
}

type TabsListProps = React.ComponentProps<typeof TabsPrimitive.List> & { color?: TabsColor };

function TabsList({ className, color = 'blue', ...props }: TabsListProps) {
  return (
    <TabsColorContext.Provider value={color}>
      <TabsPrimitive.List
        data-slot="tabs-list"
        className={cn('flex items-center gap-6 border-b border-grey-30', className)}
        {...props}
      />
    </TabsColorContext.Provider>
  );
}

function TabsTrigger({ className, ...props }: React.ComponentProps<typeof TabsPrimitive.Trigger>) {
  const color = React.useContext(TabsColorContext);

  return (
    <TabsPrimitive.Trigger
      data-slot="tabs-trigger"
      className={cn(
        tabsTriggerVariants({ color }),
        'focus-ring',
        'disabled:pointer-events-none disabled:opacity-50',
        className,
      )}
      {...props}
    />
  );
}

function TabsContent({ className, ...props }: React.ComponentProps<typeof TabsPrimitive.Content>) {
  return <TabsPrimitive.Content data-slot="tabs-content" className={cn('focus-ring', className)} {...props} />;
}

export { Tabs, TabsContent, TabsList, TabsTrigger, tabsTriggerVariants, type TabsListProps };
